import { useState } from "react";
import moment from "moment/moment";
import { IconLock, IconLockOpen } from "@tabler/icons-react";
import { Table } from "../UI/Table";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import EmptyState from "../UI/EmptyState";
import Button from "../UI/Button";
import Text from "../UI/Text";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { usePeriodesComptables } from "../../hooks/use-compta-pro";
import {
  debutClotureLabel,
  euros,
  periodeLabel,
  periodeTerminee,
} from "./comptaUtils";
import styles from "./Compta.module.css";

type ConfirmAction = { type: "close" | "reopen"; year: number; month: number };

export default function CloturesTab() {
  const toast = useToast();
  const { data, isLoading, cloturer, rouvrir } = usePeriodesComptables();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closed = data?.closed ?? [];
  const derniere = closed[0];
  const prochaine = data?.next_to_close;
  const prochaineEnCours = prochaine ? !periodeTerminee(prochaine) : false;

  async function submitConfirm() {
    if (!confirmAction) return;
    setIsSubmitting(true);
    try {
      if (confirmAction.type === "close") {
        await cloturer(confirmAction.year, confirmAction.month);
        toast.success({
          title: `Période ${periodeLabel(confirmAction)} clôturée`,
          description:
            "Les écritures sont matérialisées et les données sources verrouillées.",
        });
      } else {
        await rouvrir(confirmAction.year, confirmAction.month);
        toast.success({
          title: `Période ${periodeLabel(confirmAction)} rouverte`,
        });
      }
      setConfirmAction(null);
    } catch (error) {
      toast.error({
        title: "Action impossible",
        description: handleAPIError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={styles.stack}>
      <ModalDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        header={
          confirmAction?.type === "close"
            ? `Clôturer ${confirmAction ? periodeLabel(confirmAction) : ""} ?`
            : `Rouvrir ${confirmAction ? periodeLabel(confirmAction) : ""} ?`
        }
        size="sm"
      >
        <div className={styles.modalForm}>
          <Text>
            {confirmAction?.type === "close"
              ? "La clôture fige le journal du mois (numérotation, empreinte) et verrouille les écritures de trésorerie de la période pour tous vos mandants. Vous pourrez rouvrir cette période tant que la suivante n'est pas clôturée."
              : "La réouverture supprime les écritures matérialisées du mois et déverrouille les données sources. Le journal reste consultable en projection."}
          </Text>
        </div>
        <ModalDialogFooter>
          <Button variant="secondary" onClick={() => setConfirmAction(null)}>
            Annuler
          </Button>
          <Button onClick={submitConfirm} isLoading={isSubmitting}>
            {confirmAction?.type === "close" ? "Clôturer" : "Rouvrir"}
          </Button>
        </ModalDialogFooter>
      </ModalDialog>

      <Callout variant="info" title="Clôture mensuelle">
        Les clôtures sont strictement séquentielles depuis le premier mois de
        mandat. Chaque clôture fige le journal (numérotation par journal,
        empreinte chaînée) : c'est l'état opposable présenté à votre garant
        financier.
      </Callout>
      {isLoading || !data ? (
        <IsLoading />
      ) : (
        <>
          {prochaine && (
            <div className={styles.toolbar}>
              <Text>
                {prochaineEnCours ? (
                  <>
                    <Text as="span" weight="bold">
                      {periodeLabel(prochaine)}
                    </Text>{" "}
                    est en cours : clôturable à partir du{" "}
                    {debutClotureLabel(prochaine)}
                  </>
                ) : (
                  <>
                    Prochaine période à clôturer :{" "}
                    <Text as="span" weight="bold">
                      {periodeLabel(prochaine)}
                    </Text>
                  </>
                )}
              </Text>
              <div className={styles.toolbarSpacer}>
                <Button
                  icon={IconLock}
                  disabled={prochaineEnCours}
                  onClick={() =>
                    setConfirmAction({ type: "close", ...prochaine })
                  }
                >
                  Clôturer {periodeLabel(prochaine)}
                </Button>
              </div>
            </div>
          )}
          {closed.length === 0 ? (
            <EmptyState
              icon={IconLock}
              title="Aucune période clôturée"
              description="Clôturez vos mois au fil de l'eau : la pointe et la balance mandants deviennent opposables une fois la période figée."
            />
          ) : (
            <Table variant="card">
              <Table.Header>
                <Table.Column>Période</Table.Column>
                <Table.Column>Clôturée le</Table.Column>
                <Table.Column>Par</Table.Column>
                <Table.Column align="right">Total débit</Table.Column>
                <Table.Column align="right">Total crédit</Table.Column>
                <Table.Column>Empreinte</Table.Column>
                <Table.Column align="right">Actions</Table.Column>
              </Table.Header>
              <Table.Body>
                {closed.map((periode) => (
                  <Table.Row key={`${periode.year}-${periode.month}`}>
                    <Table.Cell>{periodeLabel(periode)}</Table.Cell>
                    <Table.Cell>
                      {periode.closed_at
                        ? moment(periode.closed_at).format("DD/MM/YYYY")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>{periode.closed_by ?? "—"}</Table.Cell>
                    <Table.Cell align="right">
                      {euros(periode.total_debit)}
                    </Table.Cell>
                    <Table.Cell align="right">
                      {euros(periode.total_credit)}
                    </Table.Cell>
                    <Table.Cell>
                      <Text as="span" variant="micro" className={styles.hash}>
                        {periode.hash.slice(0, 12)}…
                      </Text>
                    </Table.Cell>
                    <Table.Cell align="right">
                      {derniere &&
                        periode.year === derniere.year &&
                        periode.month === derniere.month && (
                          <Button
                            variant="tertiary"
                            icon={IconLockOpen}
                            onClick={() =>
                              setConfirmAction({
                                type: "reopen",
                                year: periode.year,
                                month: periode.month,
                              })
                            }
                          >
                            Rouvrir
                          </Button>
                        )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </div>
  );
}
